import { ERROR_STATUS_CODES } from "@/constants/error";
import { EVENTS } from "@/constants/socket";
import { AppRoutes } from "@/routes/config";
import { authTokenManager } from "@/services/auth-token-manager";
import { TApiError } from "@/types/api";
import { OAUTH_LOGIN_REASONS, recordOAuthBounce } from "@/utils/oauthTelemetry";
import { redirectToLogin } from "@/utils/redirectToLogin";
import { toast } from "@/utils/toast";
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_API_HOST || "http://localhost:3000";

interface ISocketContext {
  socket: Socket | null;
  connected: boolean;
}

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<ISocketContext | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);

  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }

  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const connectSocket = useCallback(() => {
    const token = authTokenManager.getToken();
    if (!token) return;

    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socketInstance = io(SOCKET_SERVER_URL, {
      extraHeaders: { Authorization: `Bearer ${token}` },
    });

    socketRef.current = socketInstance;
    setSocket(socketInstance);
  }, []);

  useEffect(() => {
    const unsubscribe = authTokenManager.onTokenChange((token) => {
      if (token) {
        connectSocket();
      } else {
        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setSocket(null);
        setConnected(false);
      }
    });

    return unsubscribe;
  }, [connectSocket]);

  useEffect(() => {
    connectSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [connectSocket]);

  useEffect(() => {
    if (!socket) return;

    const handleError = (error: TApiError) => {
      if (error.code === ERROR_STATUS_CODES.UNAUTHORIZED) {
        authTokenManager.refreshToken().then((token) => {
          if (!token) redirectToLogin({ clearToken: true, reason: OAUTH_LOGIN_REASONS.refreshFailedOn401 });
        });
      } else {
        toast.error(error.message);
      }
    };

    const handleConnected = () => {
      setConnected(true);
    };

    const handleDisconnect = () => {
      setConnected(false);
    };

    // Admin reset the password while the user is logged in (even idle): force logout silently
    const handleForceLogout = () => {
      // Same as the 403 branch: the hop goes through the local login page, so the
      // bounce is recorded here to keep a resulting loop attributable to this event.
      recordOAuthBounce(OAUTH_LOGIN_REASONS.socketForceLogout);
      authTokenManager.clearTokens();
      window.location.href = AppRoutes.login;
    };

    socket.on(EVENTS.GLOBAL.ERROR, handleError);
    socket.on(EVENTS.GLOBAL.CONNECTED, handleConnected);
    socket.on(EVENTS.AUTH.FORCE_LOGOUT, handleForceLogout);
    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off(EVENTS.GLOBAL.ERROR, handleError);
      socket.off(EVENTS.GLOBAL.CONNECTED, handleConnected);
      socket.off(EVENTS.AUTH.FORCE_LOGOUT, handleForceLogout);
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket]);

  return <SocketContext.Provider value={{ socket, connected }}>{children}</SocketContext.Provider>;
};
